"use client";

import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { sendMessageWithAttachments, type UploadProgress } from "./uploads";
import { transcribeBlob } from "./dictation";
import { gatewayHeaders, useOptionalGatewayAuth, withQueryParam } from "./gateway-auth";
import { trackEvent } from "./analytics";
import type { BillingReason } from "./billing";
import { appendDelivered, reconcileHistory, prependHistoryPage } from "./chat-reconcile";
import { releaseHeld, sendOrHold, type HeldFrames } from "./ws-held-frames";
import { RESTORED_PREFIX, restoredToolCallId } from "@/lib/active-tools";
import { contextForSend, usePanel } from "@/lib/message-context";
import { parseMessagePage, type MessagePage } from "@shared/companion/message-context";

// --- Event types (matches runtime gateway) ---

export interface WidgetPayload {
  kind: string;
  payload: Record<string, unknown>;
}

/** Sender identity for a group-chat message. Absent on private channels and
 *  on the current user's own messages. */
export interface MessageAuthor {
  id: string;
  name: string;
  picture?: string | null;
}

interface HistoryRowText {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  attachments?: MessageAttachment[];
  author?: MessageAuthor;
  /** Group @-mentions: user ids this message tags (render highlight for a
   *  tagged reader) + the matched display names (exact multi-word span
   *  emphasis without needing the roster). */
  mentions?: string[];
  mentionNames?: string[];
  /** A user message whose turn hit an LLM outage — persisted but undelivered.
   *  The UI flags it and offers a resend. */
  failed?: boolean;
  /** An agent-authored outage notice rather than a real reply. Affordances that
   *  imply the agent chose to say it (reactions) are hidden. */
  errorNotice?: boolean;
  /** A user message declined by the provider content filter — deterministic and
   *  NOT resendable. Rendered with a "declined by a safety filter" badge. */
  declined?: boolean;
  /** Optional closed-set topic label for a declined message (filter_assessment). */
  filterTopic?: string;
  /** On the agent's reply: the page it read in the person's browser to
   *  answer (@shared/companion/message-context). */
  page?: MessagePage;
}

/** An unacknowledged @-mention of the current user (dump + live tracking).
 *  Cleared only by ack_mention — never by read marks. */
export interface OutstandingMention {
  messageId: string;
  createdAt: string;
}

/** One of the channel's running or queued background tasks, as the runtime's
 *  snapshot carries it (apps/runtime/src/channel-tasks.ts). Drives the task
 *  strip above the composer. */
export type BackgroundTask = {
  id: string;
  label: string;
  triggeringMessageId: string | null;
  lastUpdate: { text: string; at: string } | null;
} & (
  /** A task handed between runtimes is `running`: for the reader it never
   *  stopped. Running first, oldest start first; then the queue in order. */
  | { state: "running"; startedAt: string }
  /** `position`: 1-based place in the agent's queue. */
  | { state: "queued"; position: number }
);

interface HistoryRowWidget {
  id: string;
  role: "widget";
  widget: WidgetPayload;
  created_at: string;
}

/** One person's emoji on a message. One per user per message — changing it
 *  replaces it (the server enforces that as a primary key). */
export interface Reaction {
  emoji: string;
  userId: string;
  /** Display name for the "who reacted" title; null when the user has none. */
  name: string | null;
}

type StreamEvent =
  | { type: "history"; messages: (HistoryRowText | HistoryRowWidget)[]; hasMore?: boolean; reactions?: Record<string, Reaction[]>; lastReadAt?: string | null; unreadCount?: number; mentions?: OutstandingMention[]; turnActive?: boolean; turnTool?: { name: string; toolCallId?: string; narration?: string; elapsedMs: number } | null; turnStatus?: string | null; backgroundTasks?: BackgroundTask[] }
  | { type: "history_page"; messages: (HistoryRowText | HistoryRowWidget)[]; hasMore?: boolean }
  // A turn nobody here typed for has started and someone is waiting on it
  // (approving a video or music request wakes the agent). Shows the agent at
  // work until `done`.
  | { type: "turn_started" }
  | { type: "text_delta"; delta: string }
  // `at`: the runtime's clock when the call started (absent from a runtime
  // that predates it).
  | { type: "tool_start"; toolName: string; toolCallId: string; args?: unknown; narration?: string; at?: number }
  | { type: "tool_end"; toolName: string; toolCallId: string; details?: Record<string, unknown> }
  // Ephemeral one-line narration of what the agent is doing right now — the
  // thinking indicator's text. Never persisted; dropped with the rest of the
  // indicator state on `done`.
  | { type: "turn_status"; status: string }
  | { type: "attachment"; url: string; path: string; alt: string; mimeType: string }
  | { type: "done"; text: string }
  | { type: "message_failed"; id: string }
  | { type: "message_declined"; id: string }
  | { type: "messages_removed"; ids: string[] }
  | { type: "message"; id?: string; content: string; attachments?: MessageAttachment[] }
  | { type: "message"; role: "user"; content: string; id: string; created_at: string; author: MessageAuthor; mentions?: string[]; mentionNames?: string[]; attachments?: MessageAttachment[] }
  | { type: "message"; role: "widget"; widget: WidgetPayload; created_at?: string }
  | { type: "approval_request"; id: string; command: string; workdir: string }
  // The browser companion's egress gate: the agent wants to do something that
  // can send data out while a page from this person's browser is in its
  // context. Never auto-answered — see components/egress-approval.tsx.
  | { type: "egress_approval_request"; id: string; tool: string; summary: string; pageUrl: string | null; expiresAt: string }
  | { type: "egress_approval_resolved"; id: string; outcome: string }
  // A sign-in the agent hands to this person (`request_user_auth`). Sent again
  // with `phase: "signing_in"` once they press Ready, and replayed on connect
  // while it is open. Never auto-answered — see components/auth-handoff.tsx.
  | { type: "auth_handoff_request"; id: string; host: string; reason: string; phase: "asking" | "signing_in"; expiresAt: string; viewEnded?: string | null; consent?: "first" | "allow_again" | null }
  | { type: "auth_handoff_resolved"; id: string; outcome: string }
  // While they sign in: the agent's browser, one base64 JPEG at a time (not
  // kept anywhere — the card shows the latest), and why the view stopped.
  | { type: "auth_handoff_frame"; id: string; data: string; sequence: number }
  | { type: "auth_handoff_view_ended"; id: string; reason: string }
  | { type: "desk_item_created"; item: unknown }
  | { type: "desk_item_updated"; item: unknown }
  // `schemaId` lets the desk keep its per-schema totals for a row its page
  // never held (a runtime from before it carries none).
  | { type: "desk_item_deleted"; itemId: string; schemaId?: string | null }
  | { type: "desk_schema_created"; item: unknown }
  | { type: "desk_schema_updated"; item: unknown }
  | { type: "desk_schema_deleted"; itemId: string }
  // `by: "agent"` marks the agent's own writes (the Files UI's carry none);
  // `at` is the runtime's clock when they happened.
  | { type: "workspace_files_changed"; paths?: string[]; removed?: boolean; by?: "agent"; at?: number }
  | { type: "deployments_changed"; deployments: unknown[] }
  | { type: "channels_changed"; channels: unknown[] }
  | { type: "budget_update"; budget_remaining: number; purchased_credit?: number }
  // `at` is the gateway's clock at broadcast time, comparable with a server
  // render's SerializedInstance.unreadAsOf — the shell uses the pair to decide
  // whether a page render or the live badge state is fresher.
  | { type: "instance_activity"; instance_id: string; channel_kind: "direct" | "group"; at?: string }
  // The instance's live portrait landed, was replaced or was removed (V486).
  // `portrait_version` is the same created_at fingerprint a page render puts
  // in SerializedInstance.portraitVersion (null = no portrait), so the shell
  // patches it in place and every avatar re-renders to the URL a reload would
  // build. Pushed to every member's sessions, whichever agent they have open.
  | { type: "instance_portrait_changed"; instance_id: string; portrait_version: string | null }
  | { type: "read_marks_updated"; instance_id: string; channel_kind: "direct" | "group"; last_read_at?: string; remaining?: number; at?: string }
  // The full reaction list for one message, pushed to everyone on the channel
  // after any change. Self-contained rather than a delta, so a client that
  // missed an event is corrected by the next one instead of drifting.
  | { type: "message_reactions"; message_id: string; reactions: Reaction[] }
  // The channel's running and queued background tasks, whole, after any of
  // them changed (spawned, started, posted a progress note, settled).
  | { type: "background_tasks"; tasks: BackgroundTask[] }
  // A message the client is already rendering optimistically now has a server
  // id. `client_key` identifies the sender's own row exactly; an assistant
  // reply carries no key and stamps the newest unstamped assistant row.
  | { type: "message_persisted"; id: string; role: "user" | "assistant"; created_at: string; client_key?: string; read_aloud_for?: string; page?: MessagePage }
  // The agent turned read-aloud on or off for this person (its `voice` tool).
  // Names the person: a group-chat socket of another member may receive it.
  | { type: "read_aloud_changed"; instance_id: string; user_id: string; read_aloud: boolean }
  | { type: "digest_status"; status: "started" | "done" | "error"; deliveredTo?: "channel" | "private"; reason?: string }
  | { type: "mention_acked"; instance_id: string; channel_kind: "direct" | "group"; message_id: string; remaining?: number }
  | { type: "mention_added"; instance_id: string; channel_kind: "direct" | "group"; message_id: string; created_at: string }
  | { type: "billing_gate"; reason: BillingReason }
  | { type: "budget_depleted"; owner: boolean; upgradeable?: boolean; renewsAt?: string }
  | { type: "telegram_bot_created"; botUsername?: string; botDisplayName?: string }
  | { type: "location_policy"; enabled?: boolean; min_interval_s?: number; min_move_m?: number; expires_at?: string | null }
  | { type: "location_request" }
  | { type: "cancel_ack"; cancelled: boolean }
  | { type: "pong" };

export interface MessageAttachment {
  /** Where the file loads from: a `blob:` preview while uploading, a
   *  gateway-token URL on the legacy path, or — with member tokens — a bare
   *  `/gateway/files/download?path=…` that the page turns into a signed link
   *  (useAttachmentUrl in chat.tsx). */
  url: string;
  /** Workspace-relative path — used to build the Files-viewer deep link. */
  path: string;
  /** Human-readable alt text for screen readers. Derived from the filename stem. */
  alt: string;
  mimeType: string;
}

export type { StreamEvent };

type AgentMode = "tide" | "flow";

/**
 * Identity + provenance carried by every rendered message.
 *
 * - `id` — server message id. Present only on rows that came from a `history`
 *   dump (the source of truth). Used as the React key and as the signal that a
 *   message is confirmed, not in-flight.
 * - `createdAt` — ISO timestamp. Server time for history rows, client time for
 *   optimistic ones. Used to order the in-flight tail against history.
 * - `pending` — true while a message is optimistic (rendered locally before
 *   the server has echoed it back in history). Cleared by being replaced with
 *   the confirmed history row on the next refresh.
 * - `key` — stable client-generated key for optimistic messages that have no
 *   server `id` yet, so React doesn't reconcile them by list position.
 */
interface MessageMeta {
  id?: string;
  createdAt?: string;
  pending?: boolean;
  key?: string;
  /** Set on a user message left undelivered by an LLM outage — rendered with an
   *  "not delivered" marker + a Resend action. Cleared on a successful resend. */
  failed?: boolean;
  /** True on an agent outage notice — see HistoryRowText.errorNotice. */
  errorNotice?: boolean;
  /** True on a user message declined by the content filter — not resendable. */
  declined?: boolean;
  /** Optional closed-set topic label for a declined message. */
  filterTopic?: string;
}

export type ChatMessage =
  | ({ role: "user" | "assistant"; content: string; attachments?: MessageAttachment[]; author?: MessageAuthor; mentions?: string[]; mentionNames?: string[]; page?: MessagePage } & MessageMeta)
  | ({ role: "widget"; widget: WidgetPayload } & MessageMeta);

// --- Subscriber registry ---

interface Subscriber {
  types: string[] | null; // null = all events
  handler: (event: StreamEvent) => void;
}

/** Stamp a freshly-rendered optimistic message with client identity so the
 *  reconcile step can tell it apart from confirmed (history) rows. */
const optimistic = (): { pending: true; createdAt: string; key: string } => ({
  pending: true,
  createdAt: new Date().toISOString(),
  key: crypto.randomUUID(),
});

// Queued-but-unsent text payloads are mirrored to sessionStorage (per-tab,
// keyed by instance) so a page reload while offline doesn't lose them — they
// flush on the next reconnect. Keyed on instanceId to match the draft store.
const outboxStorageKey = (instanceId: string, channel: string): string | null =>
  instanceId ? `chat-outbox:${instanceId}:${channel}` : null;

const readPersistedOutbox = (key: string | null): Array<Record<string, unknown>> => {
  if (!key || typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writePersistedOutbox = (key: string | null, payloads: Array<Record<string, unknown>>): void => {
  if (!key || typeof sessionStorage === "undefined") return;
  try {
    if (payloads.length > 0) sessionStorage.setItem(key, JSON.stringify(payloads));
    else sessionStorage.removeItem(key);
  } catch {}
};

/** Map wire history rows (initial dump or older page) to confirmed messages. */
const toChatMessages = (rows: (HistoryRowText | HistoryRowWidget)[]): ChatMessage[] =>
  rows.map((m) =>
    m.role === "widget"
      ? { role: "widget", widget: m.widget, id: m.id, createdAt: m.created_at }
      : {
          role: m.role,
          content: m.content,
          id: m.id,
          createdAt: m.created_at,
          ...(m.attachments && { attachments: m.attachments }),
          ...(m.author && { author: m.author }),
          ...(m.mentions && { mentions: m.mentions }),
          ...(m.mentionNames && { mentionNames: m.mentionNames }),
          ...(m.failed && { failed: true }),
          ...(m.errorNotice && { errorNotice: true }),
          ...(m.declined && { declined: true }),
          ...(typeof m.filterTopic === "string" && m.filterTopic !== "" && { filterTopic: m.filterTopic }),
          ...(m.role === "assistant" && m.page && { page: m.page }),
        },
  );

// --- Context ---

export interface SendMessageOptions {
  readonly metadata?: Record<string, unknown>;
  /** When set, the message is sent as multipart over HTTP and `files` ride
   *  along as attachments. Otherwise a plain text frame goes over the WS. */
  readonly files?: File[];
  readonly onProgress?: (p: UploadProgress) => void;
  readonly signal?: AbortSignal;
}

interface WsContextValue {
  connected: boolean;
  /** True once the first history dump has ever arrived (sticky across
   *  channel-switch reconnects). Gates the cold-load skeleton — waits for the
   *  history fetch, not merely socket-open — so the welcome never flashes before
   *  history lands, and a channel switch doesn't reflash the skeleton. */
  everLoaded: boolean;
  /** True once THIS channel's history dump has arrived; reset on a channel
   *  switch. Gates the empty/welcome state so it only renders after the current
   *  channel's fetch completes and is genuinely empty. */
  historyLoaded: boolean;
  mode: AgentMode;
  messages: ChatMessage[];
  /** Which web channel this provider is connected to: the user's private
   *  chat ("direct") or the shared group chat ("group"). */
  channel: "direct" | "group";
  /** The current (authenticated) user's id — used to distinguish "my" bubbles
   *  from other participants' in the group chat. Empty on the private channel. */
  currentUserId: string;
  /**
   * Client keys (the `key` stamped by {@link optimistic}) of optimistic
   * messages that are queued in the offline outbox — rendered locally but not
   * yet sent over the socket. Cleared when the outbox flushes on reconnect.
   */
  queuedKeys: ReadonlySet<string>;
  streaming: boolean;
  streamBuffer: string;
  /**
   * Count of agent messages that arrived while the user was "not looking" (tab
   * hidden or window blurred). Resets to 0 the moment the tab is both visible
   * and focused. Drives the favicon badge / title count.
   */
  unreadCount: number;
  /**
   * Read-state snapshot from the FIRST history dump of this channel session:
   * the server watermark and unread count as they were on entry, captured
   * before any read dwell advances the watermark. Kept across reconnects (a
   * reconnect dump must not move the unread divider mid-session); reset on a
   * channel switch. Null until the first dump. Drives the unread divider/pill.
   */
  unreadSnapshot: { lastReadAt: string | null; count: number } | null;
  /**
   * The current user's unacknowledged @-mentions on this channel, oldest
   * first. Live state (not an entry snapshot): re-seeded by every dump
   * (cross-device acks sync on reconnect), appended by live group messages
   * that tag the user, pruned by mention_acked. Cleared only by viewing the
   * mentioned message — never by mark_read.
   */
  outstandingMentions: OutstandingMention[];
  /** Emoji reactions for the loaded messages, keyed by message id. Live state
   *  (not an entry snapshot): every history dump is authoritative and each
   *  `message_reactions` event replaces one message's list wholesale. */
  reactions: Record<string, Reaction[]>;
  /** This channel's running and queued background tasks. Live state: every
   *  dump and every `background_tasks` event replaces it whole. */
  backgroundTasks: BackgroundTask[];
  /** True while an older history page exists to scroll back to. */
  hasMoreHistory: boolean;
  /** True while an older page request is in flight. */
  loadingOlderHistory: boolean;
  /** Request the page of history immediately older than what's loaded. */
  loadOlderHistory: () => void;
  billingBlocked: boolean;
  /** The owner's daily/plan budget hit zero mid-conversation (budget gate, not
   *  the billing gate — the account itself is fine). Cleared automatically
   *  when a budget_update shows spendable balance again. */
  budgetDepleted: boolean;
  /** Set with budgetDepleted when the owner is an active subscriber with a
   *  bigger plan above theirs — the notice then offers the upgrade too. */
  budgetUpgradeable: boolean;
  /** Set with budgetDepleted when the pool is PREPAID (V419): it does not
   *  refill overnight but renews with the next payment, at this instant. */
  budgetRenewsAt: string | null;
  billingReason: BillingReason | null;
  clearBillingGate: () => void;
  /** Raw WS send — sideband payloads (slack_tokens, approval_response). */
  send: (data: unknown) => void;
  /**
   * Send a chat message. Text-only goes over the WebSocket; messages with
   * `options.files` go via the runtime's `/web/message` multipart endpoint.
   * The agent's reply streams back over the same WS in either case.
   * Resolves once the message is on the wire (text) or the server has
   * accepted it (multipart). Failures throw a {@link SendMessageError}.
   */
  sendMessage: (text: string, options?: SendMessageOptions) => Promise<void>;
  /**
   * Transcribe a recorded dictation clip via the runtime's `/web/transcribe`
   * (the same speech-to-text machinery Telegram voice notes use). Resolves
   * with the transcript for the composer; nothing is sent to the agent.
   * Rejects with a typed `TranscribeError` (lib/dictation.ts).
   */
  transcribeAudio: (blob: Blob) => Promise<string>;
  /** Re-run a message left undelivered by an LLM outage. Re-triggers the
   *  existing server row (no duplicate); clears its failed marker optimistically. */
  resendMessage: (messageId: string) => void;
  subscribe: (types: string[] | null, handler: (event: StreamEvent) => void) => () => void;
}

const WsContext = createContext<WsContextValue>({
  connected: false,
  everLoaded: false,
  historyLoaded: false,
  mode: "tide",
  messages: [],
  channel: "direct",
  currentUserId: "",
  queuedKeys: new Set(),
  streaming: false,
  streamBuffer: "",
  unreadCount: 0,
  unreadSnapshot: null,
  reactions: {},
  backgroundTasks: [],
  outstandingMentions: [],
  hasMoreHistory: false,
  loadingOlderHistory: false,
  loadOlderHistory: () => {},
  billingBlocked: false,
  budgetDepleted: false,
  budgetUpgradeable: false,
  budgetRenewsAt: null,
  billingReason: null,
  clearBillingGate: () => {},
  send: () => {},
  sendMessage: async () => {},
  transcribeAudio: async () => "",
  resendMessage: () => {},
  subscribe: () => () => {},
});

const INITIAL_RETRY_MS = 1000;
const MAX_RETRY_MS = 30_000;
const CONNECT_DEBOUNCE_MS = 50;
// App-level heartbeat: the browser WebSocket API hides protocol ping/pong from
// JS, so a connection that dies without a close frame (network drop, half-open
// socket, vanished proxy) leaves `readyState` stuck at OPEN. We send our own
// ping and treat a missing pong within the window as a dead socket.
const HEARTBEAT_INTERVAL_MS = 20_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;

// --- Provider ---

export function WebSocketProvider({ wsUrl: baseWsUrl, uploadUrl: baseUploadUrl, transcribeUrl = "", instanceId = "", channel = "direct", currentUserId = "", children }: { wsUrl: string; uploadUrl: string; transcribeUrl?: string; instanceId?: string; channel?: "direct" | "group"; currentUserId?: string; children: ReactNode }) {
  // Member token (header/subprotocol) when on; null with no agent on screen,
  // and a no-op on the legacy path, whose URLs carry the gateway token.
  const gatewayAuth = useOptionalGatewayAuth();
  // Where the person is as they write: the browser side panel's page, and
  // what is visible of it when the agent sees that with every message.
  const panel = usePanel();
  // The shared group channel is the same endpoints with `channel=group`; the
  // switch reconnects in place (the URL changes, the provider stays mounted).
  const wsUrl = channel === "group" && baseWsUrl ? withQueryParam(baseWsUrl, "channel", "group") : baseWsUrl;
  const uploadUrl = channel === "group" && baseUploadUrl ? withQueryParam(baseUploadUrl, "channel", "group") : baseUploadUrl;
  const [connected, setConnected] = useState(false);
  // Sticky: true once the first history dump has ever arrived. Gates the
  // cold-load skeleton on the FETCH completing, not on socket-open (which lands
  // before history and would flash the welcome). Stays true across channel
  // switches so a reconnect doesn't reflash the skeleton; resets only when the
  // provider remounts (a different agent), since it's keyed on the instance id.
  const [everLoaded, setEverLoaded] = useState(false);
  // Per-channel: true once THIS channel's history has arrived; reset on a channel
  // switch (below). Gates the empty/welcome state so it waits for the current
  // channel's fetch instead of showing the previous channel's emptiness.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [mode, setMode] = useState<AgentMode>("tide");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingOlderHistory, setLoadingOlderHistory] = useState(false);
  const [billingBlocked, setBillingBlocked] = useState(false);
  const [budgetDepleted, setBudgetDepleted] = useState(false);
  const [budgetUpgradeable, setBudgetUpgradeable] = useState(false);
  const [budgetRenewsAt, setBudgetRenewsAt] = useState<string | null>(null);
  const [billingReason, setBillingReason] = useState<BillingReason | null>(null);
  const [queuedKeys, setQueuedKeys] = useState<ReadonlySet<string>>(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadSnapshot, setUnreadSnapshot] = useState<{ lastReadAt: string | null; count: number } | null>(null);
  const [outstandingMentions, setOutstandingMentions] = useState<OutstandingMention[]>([]);
  const [backgroundTasks, setBackgroundTasks] = useState<BackgroundTask[]>([]);
  const [reactions, setReactions] = useState<Record<string, Reaction[]>>({});

  // "Not looking" — the tab is hidden OR the window is blurred. Mirrored into a
  // ref (escape hatch, like streamingRef below) so dispatch() can read it
  // synchronously without re-subscribing on every focus/visibility change.
  const notLookingRef = useRef(false);

  const wsRef = useRef<WebSocket | null>(null);
  // Outbound text payloads accumulated while the socket is not OPEN; flushed in
  // order on reconnect. A ref (not state) because it's a transport buffer, not
  // render state — the visible pending markers live in `queuedKeys`.
  const outboxRef = useRef<Array<Record<string, unknown>>>([]);
  // Control frames sent while a socket is still CONNECTING, keyed by that
  // socket and released in its onopen. Transport buffer, like outboxRef.
  const heldFramesRef = useRef<HeldFrames>(new WeakMap());
  const mountedRef = useRef(true);
  const retryDelay = useRef(INITIAL_RETRY_MS);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pongTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const outboxRestoredRef = useRef(false);

  const outboxKey = outboxStorageKey(instanceId, channel);
  const persistOutbox = useCallback(() => {
    writePersistedOutbox(outboxKey, outboxRef.current);
  }, [outboxKey]);
  const subscribersRef = useRef<Set<Subscriber>>(new Set());
  // Attachments arrive as separate events between text_delta and done;
  // buffer them per-turn and commit alongside the message on done.
  const pendingAttachments = useRef<MessageAttachment[]>([]);

  // Track streaming state to derive mode
  const streamingRef = useRef(false);
  const thinkingRef = useRef(false);
  // The ids of tool calls currently in flight, not a boolean: a turn's tool
  // calls run in parallel and end in completion order, so "any tool_end means
  // no tool is running" is wrong (see lib/active-tools.ts).
  const activeToolsRef = useRef<Set<string>>(new Set());

  const updateMode = useCallback(() => {
    const isActive = streamingRef.current || thinkingRef.current || activeToolsRef.current.size > 0;
    setMode(isActive ? "flow" : "tide");
  }, []);

  /**
   * Track blob URLs handed out for optimistic message previews so we can
   * revoke them when their owning message goes away — either replaced by a
   * fresh history dump on reconnect, or rolled back on upload failure. The
   * unmount cleanup is the final backstop. Without per-message revocation,
   * blob URLs accumulate for the lifetime of the provider (they pin the
   * underlying File via the URL handle), which leaks memory in long sessions.
   *
   * Declared ahead of `dispatch` (which calls revokeAllBlobUrls on a fresh
   * history dump) so the callbacks exist before any consumer references them.
   */
  const blobUrlsRef = useRef<string[]>([]);
  const revokeBlobUrls = useCallback((urls: readonly string[]) => {
    if (urls.length === 0) return;
    urls.forEach((u) => { URL.revokeObjectURL(u); });
    blobUrlsRef.current = blobUrlsRef.current.filter((u) => !urls.includes(u));
  }, []);
  const revokeAllBlobUrls = useCallback(() => {
    blobUrlsRef.current.forEach((u) => { URL.revokeObjectURL(u); });
    blobUrlsRef.current = [];
  }, []);
  useEffect(() => revokeAllBlobUrls, [revokeAllBlobUrls]);

  const dispatch = useCallback((event: StreamEvent) => {
    // Update canonical message list
    switch (event.type) {
      case "message_persisted":
        // Give the optimistic row its server id, so anything keyed on one —
        // reactions — works on the message you just sent and on the reply that
        // just streamed in, instead of waiting for the next history dump.
        setMessages((prev) => {
          // Already known by this id (a delivered message carries it): nothing
          // to stamp, and the fallback below must not stamp some other row.
          if (prev.some((m) => m.id === event.id)) return prev;
          const idx = event.client_key !== undefined
            ? prev.findIndex((m) => m.key === event.client_key)
            : prev.findLastIndex((m) => m.role === event.role && m.id === undefined);
          if (idx === -1) return prev;
          const row = prev[idx];
          if (row.id !== undefined) return prev;
          // A reply that read the person's tab arrives with that page.
          const page = event.role === "assistant" ? parseMessagePage(event.page) : null;
          return prev.map((m, i) => (i === idx ? { ...m, id: event.id, createdAt: event.created_at, pending: false, ...(page && { page }) } : m));
        });
        break;
      case "message_reactions":
        // Whole-list replace: the server sends the message's full set after any
        // change, so there is no delta to merge and no way to drift.
        setReactions((prev) => ({ ...prev, [event.message_id]: event.reactions }));
        break;
      case "background_tasks":
        setBackgroundTasks(event.tasks);
        break;
      case "history":
        // The authoritative fetch has landed for this channel — gate the welcome
        // state (historyLoaded) and retire the cold-load skeleton (everLoaded).
        setHistoryLoaded(true);
        setEverLoaded(true);
        // Snapshot the entry read-state ONCE per channel session: later dumps
        // (reconnects) reflect a watermark our own dwells advanced, and using
        // them would teleport the unread divider mid-session.
        setUnreadSnapshot((prev) => prev ?? { lastReadAt: event.lastReadAt ?? null, count: event.unreadCount ?? 0 });
        // Mentions are live state, not a snapshot — every dump is
        // authoritative (acks from the user's other devices sync here).
        setOutstandingMentions(event.mentions ?? []);
        // Reactions are live state like mentions, not an entry snapshot: every
        // dump is authoritative, so a reconnect picks up whatever changed while
        // this tab was away.
        setReactions(event.reactions ?? {});
        // So are the background tasks: a reconnect after a task finished
        // must not keep showing it.
        setBackgroundTasks(event.backgroundTasks ?? []);
        if ("messages" in event) {
          const historyMessages = toChatMessages(event.messages);
          // A fresh dump resets the scrollback window to the newest page.
          setHasMoreHistory(event.hasMore === true);
          setLoadingOlderHistory(false);
          setMessages((prev) => {
            if (prev.length === 0) {
              revokeAllBlobUrls();
              return historyMessages;
            }
            // A fresh history dump is authoritative: keep only genuinely
            // in-flight optimistic messages, never resurrect rows that aged
            // out of the server's 30-message window.
            return reconcileHistory(historyMessages, prev);
          });
        }
        // A fresh dump arrives once per (re)connection and is also the
        // authoritative "is a turn in flight" reset: any streaming/thinking/
        // tool state carried over from a previous socket generation is stale —
        // the `done` that would have cleared it was missed while the socket
        // was dead (mobile tab frozen in background, turn finished, user
        // returns via the push notification). The gateway's `turnActive` says
        // whether a turn IS still running — restore "thinking" then, so a
        // mobile reconnect mid-turn doesn't blank the indicator until the
        // next stream event (a long thinking stretch or tool run can delay
        // that by minutes). Streaming state itself stays cleared — the next
        // text_delta rebuilds it with fresh content.
        setStreaming(false);
        setStreamBuffer("");
        streamingRef.current = false;
        // When the dump says the turn is inside a tool, restore the tool
        // state (chat.tsx rebuilds the full indicator from `turnTool`);
        // otherwise a running turn degrades to "thinking".
        thinkingRef.current = event.turnActive === true && !event.turnTool;
        activeToolsRef.current = new Set(
          event.turnTool ? [event.turnTool.toolCallId ?? restoredToolCallId(event.turnTool.name)] : [],
        );
        pendingAttachments.current = [];
        updateMode();
        break;

      case "history_page":
        if ("messages" in event) {
          setHasMoreHistory(event.hasMore === true);
          setLoadingOlderHistory(false);
          setMessages((prev) => prependHistoryPage(prev, toChatMessages(event.messages)));
        }
        break;

      case "attachment":
        if ("url" in event) {
          pendingAttachments.current.push({ url: event.url, path: event.path, alt: event.alt, mimeType: event.mimeType });
        }
        break;

      case "text_delta":
        if ("delta" in event && event.delta) {
          setStreaming(true);
          setStreamBuffer((prev) => prev + event.delta);
        }
        streamingRef.current = true;
        thinkingRef.current = false;
        updateMode();
        break;

      case "turn_started":
        thinkingRef.current = true;
        updateMode();
        break;

      case "tool_start":
        activeToolsRef.current.add(event.toolCallId);
        thinkingRef.current = false;
        updateMode();
        break;

      case "tool_end":
        if (!activeToolsRef.current.delete(event.toolCallId)) {
          // No exact match: this end belongs to a call we only know as a
          // reconnect placeholder (`history` gave us a tool but, on an older
          // runtime, not its id). Retire the placeholder so it can't pin the
          // mode to "flow" until the turn ends. Same fallback as endTool in
          // lib/active-tools.ts.
          const placeholder = [...activeToolsRef.current].find((id) => id.startsWith(RESTORED_PREFIX));
          if (placeholder) activeToolsRef.current.delete(placeholder);
        }
        // Only "thinking" once the WHOLE batch is done — with calls still in
        // flight the agent is working, not deliberating.
        thinkingRef.current = activeToolsRef.current.size === 0;
        setStreamBuffer((prev) => prev && !prev.endsWith("\n") ? prev + "\n\n" : prev);
        updateMode();
        break;

      case "done":
        if ("text" in event && event.text) {
          const attachments = pendingAttachments.current;
          pendingAttachments.current = [];
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: event.text,
              ...(attachments.length > 0 && { attachments }),
              ...optimistic(),
            },
          ]);
          if (notLookingRef.current) setUnreadCount((n) => n + 1);
        } else {
          pendingAttachments.current = [];
        }
        setStreaming(false);
        setStreamBuffer("");
        streamingRef.current = false;
        thinkingRef.current = false;
        activeToolsRef.current.clear();
        updateMode();
        break;

      case "mention_added":
        // An agent-authored message tagged this user — no inbound echo exists
        // for streamed replies, so the pill learns about it from this event.
        if (event.channel_kind === channel) {
          setOutstandingMentions((prev) =>
            prev.some((m) => m.messageId === event.message_id)
              ? prev
              : [...prev, { messageId: event.message_id, createdAt: event.created_at }],
          );
        }
        break;

      case "mention_acked":
        // One of the user's sessions (possibly this one) displayed the
        // mentioned message. channel_kind guards against the user-wide
        // broadcast landing on the wrong channel's provider.
        if (event.channel_kind === channel) {
          setOutstandingMentions((prev) => prev.filter((m) => m.messageId !== event.message_id));
        }
        break;

      case "message":
        if ("role" in event && event.role === "widget") {
          setMessages((prev) => [...prev, { role: "widget", widget: event.widget, ...optimistic() }]);
        } else if ("role" in event && event.role === "user") {
          // Group-chat echo of another participant's message. Ignore my own
          // (I already rendered it optimistically); render others as a
          // confirmed row (server id) with author attribution.
          if (event.author.id !== currentUserId) {
            setMessages((prev) =>
              prev.some((m) => m.id === event.id)
                ? prev
                : [...prev, { role: "user", content: event.content, id: event.id, createdAt: event.created_at, author: event.author, ...(event.mentions && { mentions: event.mentions }), ...(event.mentionNames && { mentionNames: event.mentionNames }), ...(event.attachments && event.attachments.length > 0 && { attachments: event.attachments }) }],
            );
            if (notLookingRef.current) setUnreadCount((n) => n + 1);
            // A live group message tagging me joins the outstanding set.
            if (event.mentions?.includes(currentUserId)) {
              setOutstandingMentions((prev) =>
                prev.some((m) => m.messageId === event.id)
                  ? prev
                  : [...prev, { messageId: event.id, createdAt: event.created_at }],
              );
            }
          }
        } else if ("content" in event && event.content) {
          // Broadcast/proactive messages carry their attachments in the event
          // payload itself (no separate "attachment" events to race against,
          // unlike the streaming handleMessage path). Don't consume the ref.
          // A page that loaded while it was buffered already has it from
          // history — by id, which the delivery carries — and must not show
          // it twice.
          const { id, content, attachments } = event;
          setMessages((prev) => appendDelivered(prev, {
            role: "assistant",
            content,
            ...(attachments && attachments.length > 0 && { attachments }),
            ...optimistic(),
            ...(id !== undefined && { id, pending: false }),
          }));
          if (notLookingRef.current) setUnreadCount((n) => n + 1);
        }
        break;

      case "message_failed":
        // The just-sent message hit an LLM outage. Stamp the most recent
        // still-pending user bubble with its server id and flag it undelivered,
        // so it renders a Resend affordance and a later history sync reconciles
        // it (by id) instead of duplicating. The `done` error notice follows.
        if ("id" in event) {
          setMessages((prev) => {
            const revIdx = [...prev].reverse().findIndex((m) => m.role === "user" && m.pending && !m.failed);
            if (revIdx === -1) return prev;
            const idx = prev.length - 1 - revIdx;
            return prev.map((m, i) => (i === idx ? { ...m, id: event.id, pending: false, failed: true } : m));
          });
        }
        break;

      case "message_declined":
        // The just-sent message was declined by the provider content filter.
        // `message_persisted` fires on receipt (before the turn declines), so by
        // now the bubble is usually already stamped with this id and no longer
        // pending — match by id FIRST, then fall back to the most recent pending
        // bubble. Flag `declined` (NOT `failed` — no Resend; a decline is
        // deterministic) so the badge shows live. Topic tag lands on next sync.
        if ("id" in event) {
          setMessages((prev) => {
            const byId = prev.findIndex((m) => m.id === event.id && m.role === "user");
            if (byId !== -1) {
              return prev.map((m, i) => (i === byId ? { ...m, pending: false, declined: true } : m));
            }
            const revIdx = [...prev].reverse().findIndex((m) => m.role === "user" && m.pending && !m.failed && !m.declined);
            if (revIdx === -1) return prev;
            const idx = prev.length - 1 - revIdx;
            return prev.map((m, i) => (i === idx ? { ...m, id: event.id, pending: false, declined: true } : m));
          });
        }
        break;

      case "messages_removed":
        // A previously-failed message got answered (Resend / recovery), so its
        // stale error notice was deleted server-side — drop it from the view.
        if ("ids" in event && event.ids.length > 0) {
          const gone = new Set(event.ids);
          setMessages((prev) => prev.filter((m) => !m.id || !gone.has(m.id)));
        }
        break;

      case "billing_gate":
        setBillingBlocked(true);
        setBillingReason(event.reason);
        trackEvent("Billing Gate Shown", { reason: event.reason }, { interactive: false });
        break;

      case "budget_depleted":
        // Owner-only: a member clicking a top-up CTA would credit their OWN
        // account, not this agent's owner.
        if (event.owner) {
          setBudgetDepleted(true);
          // Optional on the wire: a runtime older than the web app omits it.
          setBudgetUpgradeable(event.upgradeable === true);
          setBudgetRenewsAt(event.renewsAt ?? null);
          // Paid signal: this only fires past the billing gate, i.e. for an
          // active (paying) subscriber who's used up their plan budget — trials
          // block earlier and emit "Billing Gate Shown". PostHog identifies the
          // user, so this doubles as a paid-power-user cohort. Richer, per-user
          // outreach data lives server-side in plan_exhaustion_events.
          trackEvent("Budget Depleted Shown", { plan_state: "paid_plan_exhausted", channel }, { interactive: false });
        }
        break;

      case "budget_update":
        // Balance is back (overnight refill, a top-up, or plan change) — the
        // depleted notice clears itself.
        if (event.budget_remaining > 0 || (event.purchased_credit ?? 0) > 0) {
          setBudgetDepleted(false);
        }
        break;
    }

    for (const sub of subscribersRef.current) {
      if (sub.types === null || sub.types.includes(event.type)) {
        sub.handler(event);
      }
    }
  }, [updateMode, revokeAllBlobUrls, currentUserId, channel]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    if (pongTimer.current) {
      clearTimeout(pongTimer.current);
      pongTimer.current = null;
    }
  }, []);

  const startHeartbeat = useCallback((socket: WebSocket) => {
    stopHeartbeat();
    heartbeatTimer.current = setInterval(() => {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: "ping" }));
      if (!pongTimer.current) {
        pongTimer.current = setTimeout(() => {
          pongTimer.current = null;
          // No pong within the window — the socket is dead even though the
          // browser still reports it OPEN. Close it to trip the normal
          // onclose → reconnect/backoff path.
          socket.close();
        }, HEARTBEAT_TIMEOUT_MS);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [stopHeartbeat]);

  // Opening a socket with a member token waits for the token (a refresh, when
  // the one in hand is about to expire). Every connect and every teardown
  // advances this counter, and only the latest attempt may open a socket — so
  // a visibility/online reconnect fired during the wait, or a channel switch,
  // can never leave a second socket behind.
  const connectAttempt = useRef(0);

  const connect = useCallback(() => {
    const attempt = ++connectAttempt.current;
    const reconnectLater = () => {
      retryTimer.current = setTimeout(() => {
        retryDelay.current = Math.min(retryDelay.current * 2, MAX_RETRY_MS);
        // eslint-disable-next-line react-hooks/immutability -- intentional self-recursive reconnect; the setTimeout fires long after this useCallback is declared, so the deferred self-reference is safe
        connect();
      }, retryDelay.current);
    };

    const open = ({ url, protocols }: { url: string; protocols: string[] }) => {
      const ws = new WebSocket(url, protocols);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryDelay.current = INITIAL_RETRY_MS;
        startHeartbeat(ws);
        // Report initial page visibility — the gateway suppresses push
        // notifications only while the conversation is visibly open somewhere,
        // so every socket must declare its state up front (reconnects included).
        ws.send(JSON.stringify({ type: "visibility", visible: document.visibilityState === "visible" }));
        // Reset billing state on reconnect (user may have completed checkout in another tab)
        setBillingBlocked(false);
        setBillingReason(null);
        // Flush queued offline sends over THIS freshly-opened socket (not
        // wsRef.current, which a later close could already have nulled), in order.
        const queued = outboxRef.current;
        outboxRef.current = [];
        // At-most-once: we clear the outbox (memory + sessionStorage) as soon as
        // the frames hit the socket, before any server ack — a socket death
        // between send and server-persist drops them. Same guarantee as a normal
        // online send (an OPEN-readyState send into the kernel buffer is also
        // unacked); the persistence is about surviving reloads, not delivery.
        queued.forEach((p) => ws.send(JSON.stringify(p)));
        if (queued.length > 0) {
          persistOutbox();
          setQueuedKeys(new Set());
          setStreaming(true);
        }
        // Then the control frames sent while this socket was still connecting
        // (read marks, location fixes, … — see ws-held-frames.ts).
        releaseHeld(ws, heldFramesRef.current);
      };

      ws.onclose = (event) => {
        stopHeartbeat();
        setConnected(false);
        // The runtime closes a removed member's sockets with this reason
        // (index.ts member_removed → closeMemberSockets): re-check now, so the
        // page reloads without the agent instead of retrying until its token
        // next nears expiry.
        if (event.reason === "membership revoked") gatewayAuth?.revalidate();
        if (wsRef.current === ws && mountedRef.current) {
          wsRef.current = null;
          reconnectLater();
        }
      };

      ws.onerror = () => {};

      ws.onmessage = (e) => {
        try {
          // Any inbound frame proves the socket is alive — clear the pending
          // pong deadline regardless of message type.
          if (pongTimer.current) {
            clearTimeout(pongTimer.current);
            pongTimer.current = null;
          }
          const event = JSON.parse(e.data) as StreamEvent;
          if (event.type === "pong") return;
          dispatch(event);
        } catch {}
      };
    };

    // No agent on screen, so no credential to open the socket with.
    if (!gatewayAuth) return;
    // A member token rides the subprotocol header, fetched (or refreshed) now —
    // so a reconnect after the laptop slept never offers an expired one.
    gatewayAuth.socket(wsUrl).then(
      (target) => {
        if (mountedRef.current && attempt === connectAttempt.current) open(target);
      },
      () => {
        // No token (offline, or no longer a member): back off and try again,
        // exactly as for a dropped socket.
        if (mountedRef.current && attempt === connectAttempt.current) reconnectLater();
      },
    );
  }, [gatewayAuth, wsUrl, dispatch, startHeartbeat, stopHeartbeat, persistOutbox]);

  // Connect on mount (debounced to avoid thrash during Next.js page transitions),
  // reconnect on visibility change.
  useEffect(() => {
    if (!wsUrl) return;
    mountedRef.current = true;

    // Restore queued-but-unsent messages persisted before a reload, into the
    // outbox so the next reconnect flushes them. Must populate outboxRef before
    // connect() can fire onopen. Re-render them as pending bubbles (they survive
    // the reconnect history dump via reconcileHistory, like any optimistic row).
    if (!outboxRestoredRef.current) {
      outboxRestoredRef.current = true;
      const restored = readPersistedOutbox(outboxKey);
      if (restored.length > 0) {
        outboxRef.current = restored;
        const bubbles: ChatMessage[] = restored.map((p) => ({
          role: "user",
          content: typeof p.content === "string" ? p.content : "",
          ...optimistic(),
        }));
        /* eslint-disable react-hooks/set-state-in-effect -- restore the persisted offline outbox into the message list + queued-keys set on mount */
        setMessages((prev) => [...prev, ...bubbles]);
        setQueuedKeys((prev) => {
          const next = new Set(prev);
          bubbles.forEach((b) => b.key && next.add(b.key));
          return next;
        });
        /* eslint-enable react-hooks/set-state-in-effect */
      }
    }

    // Short delay lets Next.js finish navigation before we open the socket —
    // avoids "connection interrupted while page was loading" errors.
    const connectTimer = setTimeout(() => {
      if (mountedRef.current) connect();
    }, CONNECT_DEBOUNCE_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible" && !wsRef.current) {
        if (retryTimer.current) clearTimeout(retryTimer.current);
        retryDelay.current = INITIAL_RETRY_MS;
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // "Looking" = tab visible AND window focused. Recompute on any of the three
    // events; while looking, the unread count is meaningless, so zero it.
    const syncLooking = () => {
      const looking = document.visibilityState === "visible" && document.hasFocus();
      notLookingRef.current = !looking;
      if (looking) setUnreadCount(0);
    };
    notLookingRef.current = !(document.visibilityState === "visible" && document.hasFocus());
    document.addEventListener("visibilitychange", syncLooking);
    window.addEventListener("focus", syncLooking);
    window.addEventListener("blur", syncLooking);

    // Report visibility flips to the gateway: a hidden tab / backgrounded app
    // keeps its socket but must not suppress push notifications for this
    // conversation (the web channel is shared across the user's devices).
    const reportVisibility = () => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "visibility", visible: document.visibilityState === "visible" }));
      }
    };
    document.addEventListener("visibilitychange", reportVisibility);

    // The browser knows the network interface is gone before any socket
    // close/timeout does. Drop the connection immediately so the indicator
    // flips and outbound sends start queueing without waiting on a TCP timeout.
    const onOffline = () => {
      connectAttempt.current += 1;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      setConnected(false);
      stopHeartbeat();
      const ws = wsRef.current;
      wsRef.current = null;
      ws?.close();
    };
    const onOnline = () => {
      if (wsRef.current) return;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryDelay.current = INITIAL_RETRY_MS;
      connect();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      mountedRef.current = false;
      connectAttempt.current += 1; // an attempt still waiting for its token must not open
      clearTimeout(connectTimer);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      stopHeartbeat();
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("visibilitychange", syncLooking);
      document.removeEventListener("visibilitychange", reportVisibility);
      window.removeEventListener("focus", syncLooking);
      window.removeEventListener("blur", syncLooking);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      const ws = wsRef.current;
      wsRef.current = null;
      ws?.close();
    };
  }, [connect, stopHeartbeat, outboxKey, wsUrl]);

  // On a channel switch (Direct↔Group) the socket reconnects in place (wsUrl
  // changed) — drop the previous channel's messages so they don't linger under
  // the new one until its history arrives. Skips the initial mount so it can't
  // clobber the outbox restore above.
  const channelInitRef = useRef(true);
  useEffect(() => {
    if (channelInitRef.current) { channelInitRef.current = false; return; }
    setMessages([]);
    // The new channel's history hasn't arrived yet — suppress the welcome state
    // until it does (everLoaded stays true, so no skeleton reflash).
    setHistoryLoaded(false);
    // New channel session — the next dump re-snapshots the unread boundary
    // and re-seeds the outstanding mentions.
    setUnreadSnapshot(null);
    setOutstandingMentions([]);
    setBackgroundTasks([]);
    setStreaming(false);
    setStreamBuffer("");
    pendingAttachments.current = [];
  }, [channel]);

  // Never a bare `wsRef.current?.send`: the ref holds the socket from the moment
  // it is created, and `send` on a still-CONNECTING socket throws.
  const send = useCallback((data: unknown) => {
    sendOrHold(wsRef.current, JSON.stringify(data), heldFramesRef.current);
  }, []);

  const sendMessage = useCallback(async (text: string, options?: SendMessageOptions): Promise<void> => {
    const trimmed = text.trim();
    const files = options?.files ?? [];
    // Allow empty-text-with-files but reject the all-empty case.
    if (!trimmed && files.length === 0) return;

    // In the group chat, confirmed history rows carry the sender as `author`
    // while a bare optimistic row has none — reconcile's content-identity key
    // includes author.id, so the asymmetry would keep the pending row next to
    // its confirmed twin (a duplicate bubble after any mid-turn history dump).
    // Stamp self as the author; rendering is unaffected (own messages show no
    // author chrome — the fromOther check keys on currentUserId).
    const selfAuthor = channel === "group" && currentUserId
      ? { author: { id: currentUserId, name: "" } }
      : {};

    // The ✕ above the input covers this one message.
    const skipped = panel.skipNext;
    if (skipped) panel.setSkipNext(false);

    if (files.length === 0) {
      const meta = optimistic();
      setMessages((prev) => [...prev, { role: "user", content: trimmed, ...selfAuthor, ...meta }]);
      const payload: Record<string, unknown> = { type: "message", content: trimmed, clientKey: meta.key };
      if (options?.metadata) payload.metadata = options.metadata;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Shown at once; sent once what is visible is in hand (at most
        // VISIBLE_WAIT_MS), so it goes with the page as it was at send.
        const context = await contextForSend({ ...panel, skipNext: skipped });
        if (context) payload.context = context;
        sendOrHold(wsRef.current, JSON.stringify(payload), heldFramesRef.current);
        setStreaming(true);
        setStreamBuffer("");
      } else {
        // Sent later, so never with what is visible now.
        if (panel.context) payload.context = panel.context;
        outboxRef.current.push(payload);
        persistOutbox();
        setQueuedKeys((prev) => new Set(prev).add(meta.key));
      }
      return;
    }

    // Multipart path: optimistic render with blob URL previews — these get
    // replaced by real workspace paths on the next history refresh, at which
    // point we revoke them. Track per-call so a failure can revoke just this
    // batch without touching unrelated in-flight messages.
    const batchUrls: string[] = [];
    const optimisticAttachments: MessageAttachment[] = files.map((f) => {
      const url = URL.createObjectURL(f);
      batchUrls.push(url);
      blobUrlsRef.current.push(url);
      return {
        url,
        path: `(uploading) ${f.name}`,
        alt: f.name,
        mimeType: f.type || "application/octet-stream",
      };
    });
    setMessages((prev) => [...prev, { role: "user", content: trimmed, attachments: optimisticAttachments, ...selfAuthor, ...optimistic() }]);
    setStreaming(true);
    setStreamBuffer("");

    try {
      await sendMessageWithAttachments({
        url: uploadUrl,
        headers: await gatewayHeaders(gatewayAuth),
        text: trimmed,
        files,
        metadata: options?.metadata,
        context: await contextForSend({ ...panel, skipNext: skipped }),
        onProgress: options?.onProgress,
        signal: options?.signal,
      });
    } catch (err) {
      // Roll the optimistic message off and reclaim its preview URLs.
      setMessages((prev) => prev.slice(0, -1));
      setStreaming(false);
      revokeBlobUrls(batchUrls);
      throw err;
    }
  }, [uploadUrl, gatewayAuth, revokeBlobUrls, persistOutbox, channel, currentUserId, panel]);

  const transcribeAudio = useCallback(
    async (blob: Blob): Promise<string> => transcribeBlob(transcribeUrl, blob, await gatewayHeaders(gatewayAuth)),
    [transcribeUrl, gatewayAuth],
  );

  const resendMessage = useCallback((messageId: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "resend", messageId }));
    // Optimistically drop the failed marker and show the turn back in flight;
    // the server re-answers the existing row (a fresh reply arrives via `done`).
    // Also drop the stale error notice: a persisted one (with a server id) is
    // removed via `messages_removed`, but a live-failure notice is an optimistic
    // bubble with no id — clear those sitting right after the resent message.
    setMessages((prev) => {
      const at = prev.findIndex((m) => m.id === messageId);
      return prev
        .map((m) => (m.id === messageId ? { ...m, failed: false } : m))
        .filter((m, i) => !(at !== -1 && i > at && m.role === "assistant" && m.pending && !m.id));
    });
    setStreaming(true);
    setStreamBuffer("");
  }, []);

  const clearBillingGate = useCallback(() => {
    setBillingBlocked(false);
    setBillingReason(null);
  }, []);

  const loadOlderHistory = useCallback(() => {
    if (!hasMoreHistory || loadingOlderHistory) return;
    // Page from the oldest confirmed (server-id'd) row we hold. Optimistic
    // messages have no id and are always the newest, so they're never the cursor.
    const oldest = messages.find((m) => m.id && m.createdAt);
    if (!oldest?.id || !oldest.createdAt) return;
    const outcome = sendOrHold(
      wsRef.current,
      JSON.stringify({ type: "load_history", before: { createdAt: oldest.createdAt, id: oldest.id } }),
      heldFramesRef.current,
    );
    // A dropped request gets no reply, so it must not leave the spinner up
    // forever; the next scroll to the top asks again.
    if (outcome !== "dropped") setLoadingOlderHistory(true);
  }, [hasMoreHistory, loadingOlderHistory, messages]);

  const subscribe = useCallback((types: string[] | null, handler: (event: StreamEvent) => void) => {
    const sub: Subscriber = { types, handler };
    subscribersRef.current.add(sub);
    return () => { subscribersRef.current.delete(sub); };
  }, []);

  return (
    <WsContext.Provider value={{ connected, everLoaded, historyLoaded, mode, messages, channel, currentUserId, queuedKeys, streaming, streamBuffer, unreadCount, unreadSnapshot, outstandingMentions, reactions, backgroundTasks, hasMoreHistory, loadingOlderHistory, loadOlderHistory, billingBlocked,
      budgetDepleted, budgetUpgradeable, budgetRenewsAt, billingReason, clearBillingGate, send, sendMessage, transcribeAudio, resendMessage, subscribe }}>
      {children}
    </WsContext.Provider>
  );
}

// --- Hooks ---

export function useWs() {
  return useContext(WsContext);
}

/** Subscribe to WS events. Handler MUST be wrapped in useCallback. */
export function useWsSubscribe(types: string[] | null, handler: (event: StreamEvent) => void) {
  const { subscribe } = useWs();
  useEffect(() => subscribe(types, handler), [subscribe, types, handler]);
}

export function useAgentMode() {
  return useWs().mode;
}
